import { HitData } from 'common/store/common/search/types';
import { workspaceID } from 'common/store/common/constants';
import { getUrlParam } from 'common/util/url/urlUtil';
import { RootState } from '../../store/rootReducer';
import { ContextState } from '../../store/context/reducer';
import { USERQUESTION_INDEX } from '../../store/common/search/constants';
import {
    getLoggedInUserId,
    readDismissedForWorkspaceVisit,
    readDontShowAgainForUserAndAssessment,
} from './dontShowAgainPreference';

const AI_ASSISTED_MARKING_FIELD = 'enable_ai_assisted_marking';
const AI_ASSISTED_MARKING_MPP = 'ENABLE_AI_ASSISTED_MARKING';

/** Column ref for the AI-supported marking column, shared by the definitions and the cell. */
export const AI_ASSISTED_MARKING_COLUMN_REF = 'enableAIassistedMarking';

/** Column ref for the candidate report "Marking" column. */
export const AI_MARKING_REPORT_COLUMN_REF = 'aiMarking';

/** URL flag for the tab that shows one question's Graide marking full window. */
export const AI_MARKING_VIEW_PARAM = 'aiMarkingView';

export const isAiMarkingViewTab = (): boolean => getUrlParam(AI_MARKING_VIEW_PARAM) === 'true';

/** The marking workspaces that frame the applet: the planner's and the grader's. */
const AI_MARKING_WORKSPACES: string[] = [workspaceID.PLAN_MARKING, workspaceID.EVALUATE_MARKING];

/**
 * True when the Enable_AI_Assisted_Marking marketplace property is switched on.
 * The property is Disabled for every marketplace by default.
 */
export const isAiMarkingMarketplaceEnabled = (): boolean => {
    return Boolean((window as any).marketplaceProperties?.[AI_ASSISTED_MARKING_MPP]);
};

/**
 * True when the USERQUESTION hit is flagged for AI-assisted marking and the
 * marketplace allows it
 */
export const isAiAssistedMarkingEnabled = (hit?: HitData | null): boolean => {
    if (!isAiMarkingMarketplaceEnabled()) {
        return false;
    }
    return hit?.enableAiAssistedMarking === true || hit?.[AI_ASSISTED_MARKING_FIELD] === true;
};

// True when the submission pane should frame the Graide applet instead of the built-in
export const shouldEmbedAiMarking = (context: ContextState): boolean => {
    const {
        workspaceId, questionId, assessmentRunId, partyId,
    } = context;

    if (!AI_MARKING_WORKSPACES.includes(workspaceId)) {
        return false;
    }
    if (!(questionId > 0 && assessmentRunId > 0 && partyId > 0)) {
        return false;
    }

    const questionsList = context.partyState?.questionsList;
    if (!Array.isArray(questionsList)) {
        return false;
    }

    const hit = questionsList.find((h: { questionId?: number }) => h?.questionId === questionId);
    return isAiAssistedMarkingEnabled(hit);
};

/**
 * Intro modal for AI-supported marking. Safe to leave on: every caller still
 * requires MPP, a marking workspace, questions data, and the don't-show-again preference.
 */
const AI_INTRO_MODAL_ENABLED = true;

const readIntroUserAndAssessment = (state: RootState): { userId?: number; assessmentRunId?: number } => ({
    userId: getLoggedInUserId(),
    assessmentRunId: (state as any)?.context?.assessmentRunId,
});

const readDontShowAgainPreference = (state: RootState): boolean => {
    const { userId, assessmentRunId } = readIntroUserAndAssessment(state);
    return readDontShowAgainForUserAndAssessment(userId, assessmentRunId);
};

const getUserQuestionHits = (state: RootState): any[] | undefined => {
    const uqPrimary = (state as any)?.context?.partyState?.questionsList;
    if (Array.isArray(uqPrimary)) {
        return uqPrimary;
    }

    const currentFooterFeatureId = `feature_footer_${(state as any)?.context?.workspaceId}`;
    const footerState: any = (state as any)?.[currentFooterFeatureId];
    if (!footerState?.navigation) {
        return undefined;
    }
    const navEntries = Object.values(footerState.navigation) as any[];
    const uqNav = navEntries.find((nav: any) => nav?.dataType === USERQUESTION_INDEX && Array.isArray(nav?.hits));
    return uqNav ? (uqNav.hits as any[]) : undefined;
};

export const shouldOpenAiIntro = (state: RootState, workspaceId?: string): boolean => {
    try {
        if (!AI_INTRO_MODAL_ENABLED) return false;
        if (!isAiMarkingMarketplaceEnabled()) return false;
        const wsId = workspaceId || (state as any)?.context?.workspaceId;
        if (!AI_MARKING_WORKSPACES.includes(wsId)) return false;
        if (readDontShowAgainPreference(state)) return false;
        const { userId, assessmentRunId } = readIntroUserAndAssessment(state);
        if (readDismissedForWorkspaceVisit(userId, assessmentRunId)) return false;

        const uqHits = getUserQuestionHits(state);
        if (!Array.isArray(uqHits) || uqHits.length === 0) return false;
        return uqHits.some((h: any) => isAiAssistedMarkingEnabled(h));
    } catch (e) {
        console.error('Could not resolve AI marking intro state', e);
        return false;
    }
};

export default shouldOpenAiIntro;
